// Importing necessary dependencies and components
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, UserCircle, CheckCircle } from 'lucide-react';
import { REGIONS_FRANCE } from '../constants/region';
import { POSITIONS } from '../constants/positions';
import { Card } from '../components/ui/Card';
import { FormField } from '../components/ui/FormField';
import { StepIndicator } from '../components/ui/StepIndicator';

// Define constants for registration steps and legal structures
const STEPS = ['Entreprise', 'Responsable', 'Confirmation'];
const LEGAL_STRUCTURES = ['SARL', 'SAS', 'EURL', 'SA'];

// Interfaces for form structure
interface BusinessForm {
  company_name: string;
  phone_number: string;
  email: string;
  company_address: string;
  siret_number: string;
  legal_structure: string;
  area: string;
}

interface ResponsibleForm {
  full_name: string;
  position: string;
  phone_number: string;
  email: string;
  password: string;
  confirm_password: string;
}

// Main Register component
export function Register() {
  const navigate = useNavigate(); // For navigation between pages
  const [step, setStep] = useState(1); // Track current step
  const [loading, setLoading] = useState(false); // Loading state
  const [error, setError] = useState(''); // Error messages
  const [businessId, setBusinessId] = useState<number | null>(null); // ID of created business

  // State for business form fields
  const [businessForm, setBusinessForm] = useState<BusinessForm>({
    company_name: '',
    phone_number: '',
    email: '',
    company_address: '',
    siret_number: '',
    legal_structure: '',
    area: '',
  });

  // State for responsible form fields
  const [responsibleForm, setResponsibleForm] = useState<ResponsibleForm>({
    full_name: '',
    position: '',
    phone_number: '',
    email: '',
    password: '',
    confirm_password: '',
  });

  // Handle submission of business form
  const handleBusinessSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Send business data to the API
      const response = await fetch('https://x1lc-aku3-stjs.p7.xano.io/api:moW4brDB/business', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...businessForm,
          siret_number: parseInt(businessForm.siret_number), // Ensure SIRET is numeric
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error creating business');
      }

      setBusinessId(data.id); // Save the created business ID
      setStep(2); // Move to step 2
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false); // Stop loading
    }
  };

  // Handle submission of responsible form
  const handleResponsibleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (responsibleForm.password !== responsibleForm.confirm_password) {
      setError('Passwords do not match'); // Validate password match
      return;
    }

    setError('');
    setLoading(true);

    try {
      // Send responsible data to the API
      const responsibleResponse = await fetch('https://x1lc-aku3-stjs.p7.xano.io/api:O20NksVB/responsibles', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          business_id: businessId, // Link to the created business
          full_name: responsibleForm.full_name,
          position: responsibleForm.position,
          phone_number: responsibleForm.phone_number,
          email: responsibleForm.email,
        }),
      });

      if (!responsibleResponse.ok) {
        throw new Error('Error creating responsible');
      }

      // Create a user account for the responsible
      const authResponse = await fetch('https://x1lc-aku3-stjs.p7.xano.io/api:O20NksVB/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: responsibleForm.email,
          password: responsibleForm.password,
          name: responsibleForm.full_name,
        }),
      });

      if (!authResponse.ok) {
        throw new Error('Error creating user account');
      }

      setStep(3); // Move to the confirmation step
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false); // Stop loading
    }
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-lightest to-background-light py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary-dark">
            Créer votre compte JobBoard
          </h1>
          <p className="mt-2 text-text-secondary">
            Rejoignez notre plateforme de recrutement spécialisée
          </p>
        </div>

        <StepIndicator steps={STEPS} currentStep={step} />

        {error && (
          <div className="mb-6 bg-accent-error-light border-l-4 border-accent-error p-4 rounded-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-accent-error" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <Card>
            <div className="flex items-center mb-6">
              <Building2 className="h-6 w-6 text-primary-main mr-2" />
              <h2 className="text-2xl font-bold text-primary-dark">
                Information de l'entreprise
              </h2>
            </div>

            <form onSubmit={handleBusinessSubmit} className="space-y-6">
              <FormField
                label="Nom de l'entreprise"
                name="company_name"
                value={businessForm.company_name}
                onChange={(e) => setBusinessForm({ ...businessForm, company_name: e.target.value })}
                required
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  label="Téléphone"
                  type="tel"
                  name="phone_number"
                  value={businessForm.phone_number}
                  onChange={(e) => setBusinessForm({ ...businessForm, phone_number: e.target.value })}
                  required
                />

                <FormField
                  label="Email"
                  type="email"
                  name="email"
                  value={businessForm.email}
                  onChange={(e) => setBusinessForm({ ...businessForm, email: e.target.value })}
                  required
                />
              </div>

              <FormField
                label="Adresse"
                name="company_address"
                value={businessForm.company_address}
                onChange={(e) => setBusinessForm({ ...businessForm, company_address: e.target.value })}
                required
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  label="Numéro SIRET"
                  name="siret_number"
                  value={businessForm.siret_number}
                  onChange={(e) => setBusinessForm({ ...businessForm, siret_number: e.target.value })}
                  required
                />

                <FormField
                  label="Structure juridique"
                  name="legal_structure"
                  value={businessForm.legal_structure}
                  onChange={(e) => setBusinessForm({ ...businessForm, legal_structure: e.target.value })}
                  options={LEGAL_STRUCTURES.map((structure) => ({
                    value: structure,
                    label: structure,
                  }))}
                  required
                />
              </div>

              <FormField
                label="Région"
                name="area"
                value={businessForm.area}
                onChange={(e) => setBusinessForm({ ...businessForm, area: e.target.value })}
                options={REGIONS_FRANCE.map((region) => ({
                  value: region,
                  label: region,
                }))}
                required
              />

              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-main hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light disabled:opacity-50 transition-colors"
              >
                {loading ? 'Chargement...' : 'Suivant'}
              </button>
            </form>
          </Card>
        )}

        {step === 2 && (
          <Card>
            <div className="flex items-center mb-6">
              <UserCircle className="h-6 w-6 text-primary-main mr-2" />
              <h2 className="text-2xl font-bold text-primary-dark">
                Information du responsable
              </h2>
            </div>

            <form onSubmit={handleResponsibleSubmit} className="space-y-6">
              <FormField
                label="Nom complet"
                name="full_name"
                value={responsibleForm.full_name}
                onChange={(e) => setResponsibleForm({ ...responsibleForm, full_name: e.target.value })}
                required
              />

              <FormField
                label="Poste"
                name="position"
                value={responsibleForm.position}
                onChange={(e) => setResponsibleForm({ ...responsibleForm, position: e.target.value })}
                options={POSITIONS.map((position) => ({
                  value: position,
                  label: position,
                }))}
                required
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  label="Téléphone"
                  type="tel"
                  name="phone_number"
                  value={responsibleForm.phone_number}
                  onChange={(e) => setResponsibleForm({ ...responsibleForm, phone_number: e.target.value })}
                  required
                />

                <FormField
                  label="Email"
                  type="email"
                  name="email"
                  value={responsibleForm.email}
                  onChange={(e) => setResponsibleForm({ ...responsibleForm, email: e.target.value })}
                  required
                />
              </div>

              <FormField
                label="Mot de passe"
                type="password"
                name="password"
                value={responsibleForm.password}
                onChange={(e) => setResponsibleForm({ ...responsibleForm, password: e.target.value })}
                required
              />

              <FormField
                label="Confirmer le mot de passe"
                type="password"
                name="confirm_password"
                value={responsibleForm.confirm_password}
                onChange={(e) => setResponsibleForm({ ...responsibleForm, confirm_password: e.target.value })}
                required
              />

              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="inline-flex justify-center py-3 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-text-secondary bg-background-light hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light transition-colors"
                >
                  Retour
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-main hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light disabled:opacity-50 transition-colors"
                >
                  {loading ? 'Chargement...' : 'Créer le compte'}
                </button>
              </div>
            </form>
          </Card>
        )}

        {step === 3 && (
          <Card className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-accent-success-light">
              <CheckCircle className="h-6 w-6 text-accent-success" />
            </div>
            <h2 className="mt-6 text-2xl font-bold text-primary-dark">
              Inscription réussie!
            </h2>
            <p className="mt-2 text-text-secondary">
              Votre compte a été créé avec succès. Vous pouvez maintenant vous connecter.
            </p>
            <div className="mt-6">
              <button
                onClick={() => navigate('/login')}
                className="inline-flex justify-center py-3 px-6 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-main hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light transition-colors"
              >
                Se connecter
              </button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}


